import { useState, useCallback } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useUser } from "@clerk/clerk-react";
import { useApiAuth } from "./useApiAuth";
import type { PoolJoinResponse } from "../types/pools";
import { apiUrl } from "../lib/api";

interface UsePoolJoinProps {
  onSuccess: (poolCode: string) => void;
  onReloadPools: () => Promise<void>;
  onLogin: () => void;
}

export function usePoolJoin({ onSuccess, onReloadPools, onLogin }: UsePoolJoinProps) {
  const { isSignedIn: isAuthenticated } = useUser();
  const { acquireToken } = useApiAuth();

  const [joinPoolCode, setJoinPoolCode] = useState("");
  const [joinPoolFeedback, setJoinPoolFeedback] = useState<string | null>(null);
  const [joinPoolFeedbackKind, setJoinPoolFeedbackKind] = useState<"error" | "success" | "info" | null>(null);
  const [isJoiningPool, setJoiningPool] = useState(false);

  const handleJoinPoolCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.toUpperCase();
      setJoinPoolCode(value);
      if (joinPoolFeedback) {
        setJoinPoolFeedback(null);
      }
      if (joinPoolFeedbackKind) {
        setJoinPoolFeedbackKind(null);
      }
    },
    [joinPoolFeedback, joinPoolFeedbackKind]
  );

  const handleJoinPoolSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedCode = joinPoolCode.trim();
      if (!trimmedCode) {
        setJoinPoolFeedbackKind("error");
        setJoinPoolFeedback("Enter a pool code to continue.");
        return;
      }
      if (!isAuthenticated) {
        setJoinPoolFeedbackKind("error");
        setJoinPoolFeedback("Sign in to join a pool.");
        onLogin();
        return;
      }

      const normalizedCode = trimmedCode.toUpperCase();
      const joinUrl = apiUrl("/api/pools/join");

      setJoinPoolFeedback(null);
      setJoinPoolFeedbackKind(null);
      setJoiningPool(true);

      try {
        const token = await acquireToken();
        if (!token) {
          setJoinPoolFeedbackKind("error");
          setJoinPoolFeedback("Authentication required. Please sign in again.");
          return;
        }

        const response = await fetch(joinUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ poolCode: normalizedCode }),
        });

        if (response.status === 401 || response.status === 403) {
          setJoinPoolFeedbackKind("error");
          setJoinPoolFeedback("Your session expired. Please sign in again.");
          onLogin();
          return;
        }

        if (!response.ok) {
          let message: string | null = null;
          try {
            const payload = await response.json();
            if (payload?.detail) {
              message = String(payload.detail);
            }
          } catch (jsonError) {
            console.debug("Failed to parse join pool error payload", jsonError);
          }

          if (response.status === 404) {
            setJoinPoolFeedbackKind("error");
            setJoinPoolFeedback(message ?? "Pool code not found. Check the code and try again.");
            return;
          }

          if (response.status === 409) {
            if (message && /already/i.test(message)) {
              setJoinPoolFeedbackKind("info");
              setJoinPoolFeedback(message);
              await onReloadPools();
              return;
            }
            setJoinPoolFeedbackKind("error");
            setJoinPoolFeedback(message ?? "Unable to join this pool right now.");
            return;
          }

          throw new Error(message ?? `Unable to join pool (${response.status}).`);
        }

        const membership: PoolJoinResponse = await response.json();
        if (membership.membership_status === "PENDING") {
          setJoinPoolFeedbackKind("info");
          setJoinPoolFeedback(
            `Your request to join pool ${membership.pool_code} is pending admin approval.`
          );
        } else {
          setJoinPoolFeedbackKind("success");
          setJoinPoolFeedback(`Joined pool ${membership.pool_code}. Loading your data...`);
          onSuccess(membership.pool_code);
        }
        setJoinPoolCode("");
        await onReloadPools();
      } catch (error) {
        console.error("Failed to join pool", error);
        setJoinPoolFeedbackKind("error");
        setJoinPoolFeedback(error instanceof Error ? error.message : "Unable to join pool right now.");
      } finally {
        setJoiningPool(false);
      }
    },
    [joinPoolCode, isAuthenticated, acquireToken, onLogin, onReloadPools, onSuccess]
  );

  const resetFeedback = useCallback(() => {
    // Note: Don't clear joinPoolCode here - it might be pre-filled from an invitation link
    setJoinPoolFeedback(null);
    setJoinPoolFeedbackKind(null);
  }, []);

  const resetAll = useCallback(() => {
    setJoinPoolCode("");
    setJoinPoolFeedback(null);
    setJoinPoolFeedbackKind(null);
  }, []);

  return {
    joinPoolCode,
    setJoinPoolCode,
    joinPoolFeedback,
    joinPoolFeedbackKind,
    isJoiningPool,
    handleJoinPoolCodeChange,
    handleJoinPoolSubmit,
    resetFeedback,
    resetAll,
  };
}
